import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Plus,
  Pencil,
  Search,
  MoreHorizontal,
  Workflow,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const AssetTypesTab = ({ api, equipmentTypes, fetchEquipmentTypes, setupProfiles = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    is_active: true,
    is_combo: false,
    combo_component_ids: [],
    use_component_forms: false,
    requires_setup: false,
    setup_profile_ids: [],
    default_setup_profile_id: null,
    requires_transport: false,
    transport_equipment_type_id: null,
    transport_offset_minutes: -60,
    transport_return_required: false,
    requires_prime_mover: false,
    prime_mover_asset_type_id: null,
    trailer_count: 1
  });

  const activeCount = equipmentTypes.filter(t => t.is_active).length;
  const inactiveCount = equipmentTypes.filter(t => !t.is_active).length;
  const customCount = equipmentTypes.filter(t => !t.is_system).length;
  const systemCount = equipmentTypes.filter(t => t.is_system).length;
  const comboCount = equipmentTypes.filter(t => t.is_combo).length;
  const activeProfiles = setupProfiles.filter(p => p.is_active);

  const filteredTypes = equipmentTypes.filter(type => {
    const matchesSearch = type.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || 
      (typeFilter === 'system' && type.is_system) || 
      (typeFilter === 'custom' && !type.is_system);
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && type.is_active) || 
      (statusFilter === 'inactive' && !type.is_active);
    return matchesSearch && matchesType && matchesStatus;
  });

  const resetForm = () => {
    setForm({
      name: '', description: '', is_active: true, is_combo: false, combo_component_ids: [], use_component_forms: false,
      requires_setup: false, setup_profile_ids: [], default_setup_profile_id: null,
      requires_transport: false, transport_equipment_type_id: null,
      transport_offset_minutes: -60, transport_return_required: false,
      requires_prime_mover: false, prime_mover_asset_type_id: null, trailer_count: 1
    });
    setEditingType(null);
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setForm({
        name: type.name,
        description: type.description || '',
        is_active: type.is_active,
        is_combo: type.is_combo || false,
        combo_component_ids: type.combo_component_ids || [],
        alternative_groups: [],
        use_component_forms: type.use_component_forms || false,
        requires_setup: type.requires_setup || false,
        setup_profile_ids: type.setup_profile_ids || [],
        default_setup_profile_id: type.default_setup_profile_id || null,
        requires_transport: type.requires_transport || false,
        transport_equipment_type_id: type.transport_equipment_type_id || null,
        transport_offset_minutes: type.transport_offset_minutes ?? -60,
        transport_return_required: type.transport_return_required || false,
        requires_prime_mover: type.requires_prime_mover || false,
        prime_mover_asset_type_id: type.prime_mover_asset_type_id || null,
        trailer_count: type.trailer_count || 1
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (form.is_combo && form.combo_component_ids.length < 2) {
        toast.error('Combo types must have at least 2 component equipment types');
        return;
      }
      
      const submitData = {
        name: form.name,
        description: form.description,
        is_active: form.is_active,
        is_combo: form.is_combo,
        combo_component_ids: form.combo_component_ids,
        alternative_groups: [],
        use_component_forms: form.is_combo ? form.use_component_forms : false,
        requires_setup: form.requires_setup,
        setup_profile_ids: form.requires_setup ? form.setup_profile_ids : [],
        default_setup_profile_id: form.requires_setup ? form.default_setup_profile_id : null,
        requires_transport: form.requires_transport,
        transport_equipment_type_id: form.requires_transport ? form.transport_equipment_type_id : null,
        transport_offset_minutes: form.requires_transport ? Number(form.transport_offset_minutes) : -60,
        transport_return_required: form.requires_transport ? form.transport_return_required : false,
        requires_prime_mover: form.requires_prime_mover,
        prime_mover_asset_type_id: form.requires_prime_mover ? form.prime_mover_asset_type_id : null,
        trailer_count: form.requires_prime_mover ? Math.max(1, Math.min(4, Number(form.trailer_count) || 1)) : 1
      };

      if (editingType) {
        await api.put(`/equipment-types/${editingType.id}`, submitData);
        toast.success('Asset type updated');
      } else {
        await api.post('/equipment-types', submitData);
        toast.success('Asset type created');
      }
      setDialogOpen(false);
      resetForm();
      fetchEquipmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = (type) => {
    if (type.is_system) {
      toast.error('System types cannot be archived. You can deactivate them instead.');
      return;
    }
    setDeleteTarget(type);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/equipment-types/${deleteTarget.id}`);
      toast.success('Asset type archived');
      fetchEquipmentTypes();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.reasons) {
        toast.error(detail.message);
      } else {
        toast.error((typeof detail === 'string' && detail) || 'Archive failed');
      }
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleRestore = async (type) => {
    try {
      await api.post(`/equipment-types/${type.id}/restore`);
      toast.success('Asset type restored');
      fetchEquipmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    }
  };

  const handleToggleActive = async (type) => {
    try {
      await api.put(`/equipment-types/${type.id}`, {
        name: type.name,
        is_active: !type.is_active
      });
      toast.success(`Asset type ${type.is_active ? 'deactivated' : 'activated'}`);
      fetchEquipmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const getAvailableComboComponents = () => {
    return equipmentTypes.filter(t => 
      t.is_active && 
      !t.is_combo && 
      t.id !== editingType?.id
    );
  };

  const getComboComponentNames = (componentIds) => {
    if (!componentIds || componentIds.length === 0) return '-';
    return componentIds.map(id => {
      const type = equipmentTypes.find(t => t.id === id);
      return type?.name || id;
    }).join(' + ');
  };

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Asset Types</CardTitle>
              <CardDescription>
                {filteredTypes.length} of {equipmentTypes.length} shown — {activeCount} active, {inactiveCount} inactive, {customCount} custom{comboCount > 0 && `, ${comboCount} combo`}
              </CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-type-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Type
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="type-search"
                placeholder="Search types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="system">System ({systemCount})</SelectItem>
                <SelectItem value="custom">Custom ({customCount})</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Active" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active ({activeCount})</SelectItem>
                <SelectItem value="inactive">Inactive ({inactiveCount})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="label-overline">Name</TableHead>
                  <TableHead className="label-overline">Type</TableHead>
                  <TableHead className="label-overline">Combo Components</TableHead>
                  <TableHead className="label-overline">Setup Profile</TableHead>
                  <TableHead className="label-overline">Status</TableHead>
                  <TableHead className="label-overline text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTypes.map((type) => (
                  <TableRow key={type.id} data-testid={`type-row-${type.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {type.name}
                        {type.is_combo && (
                          <Badge className="bg-purple-500/20 text-purple-600 text-xs">Combo</Badge>
                        )}
                        {type.archived && (
                          <Badge variant="outline" className="text-xs text-muted-foreground border-muted-foreground/40 bg-muted/30">Archived</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {type.is_system ? (
                        <Badge variant="secondary">System</Badge>
                      ) : (
                        <Badge className="bg-[#ffd108]/20 text-[#ffd108]">Custom</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[250px]">
                      {type.is_combo ? (
                        <span className="text-purple-600">
                          {getComboComponentNames(type.combo_component_ids)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {type.requires_setup && (type.setup_profile_ids || []).length > 0 ? (
                        <div className="flex items-center gap-1.5">
                          <Workflow className="h-3.5 w-3.5 text-blue-500" />
                          <span className="text-blue-500 text-xs">
                            {(type.setup_profile_ids || []).map(pid => {
                              const p = setupProfiles.find(pr => pr.id === pid);
                              return p?.name;
                            }).filter(Boolean).join(', ') || 'Assigned'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch 
                        checked={type.is_active}
                        onCheckedChange={() => handleToggleActive(type)}
                        data-testid={`type-status-${type.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenDialog(type)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {!type.is_system && (
                            <>
                              <DropdownMenuSeparator />
                              {type.archived ? (
                                <DropdownMenuItem onClick={() => handleRestore(type)}>
                                  <ArchiveRestore className="h-4 w-4 mr-2" />
                                  Restore
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem 
                                  className="text-destructive"
                                  onClick={() => handleDelete(type)}
                                >
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive
                                </DropdownMenuItem>
                              )}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Equipment Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingType ? 'Edit Asset Type' : 'Add Asset Type'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="type-form">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Excavator 20t"
                required
                disabled={editingType?.is_system}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_combo"
                checked={form.is_combo}
                onCheckedChange={(checked) => setForm({ 
                  ...form, 
                  is_combo: checked,
                  combo_component_ids: checked ? form.combo_component_ids : []
                })}
                disabled={editingType?.is_system}
              />
              <Label htmlFor="is_combo" className="text-sm">
                Combo type (combines multiple equipment types)
              </Label>
            </div>
            
            {form.is_combo && (
              <div className="space-y-2">
                <Label>Component Equipment Types</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select at least 2 equipment types that make up this combo
                </p>
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {getAvailableComboComponents().map(type => (
                    <div key={type.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`combo-${type.id}`}
                        checked={form.combo_component_ids.includes(type.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setForm({ 
                              ...form, 
                              combo_component_ids: [...form.combo_component_ids, type.id] 
                            });
                          } else {
                            setForm({ 
                              ...form, 
                              combo_component_ids: form.combo_component_ids.filter(id => id !== type.id),
                            });
                          }
                        }}
                      />
                      <Label htmlFor={`combo-${type.id}`} className="text-sm font-normal">
                        {type.name}
                      </Label>
                    </div>
                  ))}
                </div>
                {form.combo_component_ids.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Selected: {getComboComponentNames(form.combo_component_ids)}
                  </p>
                )}

                {/* Per-combo toggle: use individual component forms */}
                <div className="flex items-center space-x-2 pt-2 border-t border-muted">
                  <Checkbox
                    id="use_component_forms"
                    checked={form.use_component_forms}
                    onCheckedChange={(checked) => setForm({ ...form, use_component_forms: checked })}
                    data-testid="use-component-forms-checkbox"
                  />
                  <div>
                    <Label htmlFor="use_component_forms" className="text-sm">
                      Require individual component forms
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      When on, operators see forms for each component asset type (e.g. separate prestarts for the excavator and the tipper). When off, only forms for the combo type itself are shown.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Setup Profile */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="requires_setup"
                checked={form.requires_setup}
                onCheckedChange={(checked) => setForm({
                  ...form,
                  requires_setup: checked,
                  setup_profile_ids: checked ? form.setup_profile_ids : [],
                  default_setup_profile_id: checked ? form.default_setup_profile_id : null
                })}
                data-testid="requires-setup-checkbox"
              />
              <Label htmlFor="requires_setup" className="text-sm">
                Requires setup profile (mobilisation workflow)
              </Label>
            </div>

            {form.requires_setup && (
              <div className="space-y-3 pl-6 border-l-2 border-muted">
                {activeProfiles.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No setup profiles found. Create one in the Setup Profiles tab first.
                  </p>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs">Available Profiles</Label>
                      <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2 mt-1">
                        {activeProfiles.map(profile => (
                          <div key={profile.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`profile-${profile.id}`}
                              checked={form.setup_profile_ids.includes(profile.id)}
                              onCheckedChange={(checked) => {
                                const newIds = checked
                                  ? [...form.setup_profile_ids, profile.id]
                                  : form.setup_profile_ids.filter(id => id !== profile.id);
                                const newDefault = !checked && form.default_setup_profile_id === profile.id
                                  ? (newIds[0] || null)
                                  : (form.default_setup_profile_id || (checked ? profile.id : null));
                                setForm({ ...form, setup_profile_ids: newIds, default_setup_profile_id: newDefault });
                              }}
                            />
                            <Label htmlFor={`profile-${profile.id}`} className="text-sm font-normal flex items-center gap-2">
                              {profile.name}
                              <span className="text-xs text-muted-foreground">({(profile.steps || []).length} steps)</span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    {form.setup_profile_ids.length > 1 && (
                      <div>
                        <Label className="text-xs">Default Profile</Label>
                        <Select
                          value={form.default_setup_profile_id || ''}
                          onValueChange={val => setForm({ ...form, default_setup_profile_id: val })}
                        >
                          <SelectTrigger className="mt-1" data-testid="default-profile-select">
                            <SelectValue placeholder="Select default" />
                          </SelectTrigger>
                          <SelectContent>
                            {form.setup_profile_ids.map(pid => {
                              const p = activeProfiles.find(pr => pr.id === pid);
                              return p ? <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem> : null;
                            })}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">Auto-suggested when booking this asset type</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Transport Requirements — Mobilisation Phase 2 */}
            <div className="flex items-center space-x-2 pt-2 border-t">
              <Checkbox
                id="requires_transport"
                checked={form.requires_transport}
                disabled={form.requires_prime_mover}
                onCheckedChange={(checked) => setForm({
                  ...form,
                  requires_transport: checked,
                  transport_equipment_type_id: checked ? form.transport_equipment_type_id : null,
                  transport_return_required: checked ? form.transport_return_required : false,
                  // Mutual exclusivity
                  requires_prime_mover: checked ? false : form.requires_prime_mover,
                  prime_mover_asset_type_id: checked ? null : form.prime_mover_asset_type_id,
                })}
                data-testid="requires-transport-checkbox"
              />
              <Label htmlFor="requires_transport" className={`text-sm ${form.requires_prime_mover ? 'text-muted-foreground' : ''}`}>
                Requires transport (float truck / low-loader — creates a separate transport job)
              </Label>
            </div>

            {form.requires_transport && (
              <div className="space-y-3 pl-6 border-l-2 border-muted">
                <div>
                  <Label className="text-xs">Transport Asset Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={form.transport_equipment_type_id || ''}
                    onValueChange={val => setForm({ ...form, transport_equipment_type_id: val })}
                  >
                    <SelectTrigger className="mt-1" data-testid="transport-asset-type-select">
                      <SelectValue placeholder="Select transport asset (e.g. Float Truck 30T)" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipmentTypes
                        .filter(t => t.is_active && !t.requires_transport && t.id !== editingType?.id)
                        .map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Only assets that do not themselves require transport are shown
                  </p>
                </div>
                <div>
                  <Label className="text-xs" htmlFor="transport_offset">Transport Offset (minutes)</Label>
                  <Input
                    id="transport_offset"
                    type="number"
                    step="15"
                    className="mt-1"
                    value={form.transport_offset_minutes}
                    onChange={e => setForm({ ...form, transport_offset_minutes: e.target.value })}
                    data-testid="transport-offset-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Negative = transport arrives before primary job start (e.g. -60 for 1hr earlier)
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="transport_return_required"
                    checked={form.transport_return_required}
                    onCheckedChange={(checked) => setForm({ ...form, transport_return_required: checked })}
                    data-testid="transport-return-checkbox"
                  />
                  <Label htmlFor="transport_return_required" className="text-sm">
                    Also auto-create return leg (Demobilisation)
                  </Label>
                </div>
              </div>
            )}

            {/* Prime Mover — Paired Asset (Mobilisation Phase 2.1) */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="requires_prime_mover"
                checked={form.requires_prime_mover}
                disabled={form.requires_transport}
                onCheckedChange={(checked) => setForm({
                  ...form,
                  requires_prime_mover: checked,
                  prime_mover_asset_type_id: checked ? form.prime_mover_asset_type_id : null,
                  // Mutual exclusivity
                  requires_transport: checked ? false : form.requires_transport,
                  transport_equipment_type_id: checked ? null : form.transport_equipment_type_id,
                  transport_return_required: checked ? false : form.transport_return_required,
                })}
                data-testid="requires-prime-mover-checkbox"
              />
              <Label htmlFor="requires_prime_mover" className={`text-sm ${form.requires_transport ? 'text-muted-foreground' : ''}`}>
                Requires prime mover (e.g. Semi Trailer needs a Truck — allocated as a pair on same job)
              </Label>
            </div>

            {form.requires_prime_mover && (
              <div className="space-y-3 pl-6 border-l-2 border-muted">
                <div>
                  <Label className="text-xs">Prime Mover Asset Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={form.prime_mover_asset_type_id || ''}
                    onValueChange={val => setForm({ ...form, prime_mover_asset_type_id: val })}
                  >
                    <SelectTrigger className="mt-1" data-testid="prime-mover-asset-type-select">
                      <SelectValue placeholder="Select prime mover asset (e.g. Truck)" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipmentTypes
                        .filter(t => t.is_active && !t.requires_transport && !t.requires_prime_mover && t.id !== editingType?.id)
                        .map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    This asset will require a paired prime mover allocation on the same job. One operator drives the pair; invoice shows only the primary asset line.
                  </p>
                </div>
                <div>
                  <Label className="text-xs" htmlFor="trailer_count">Number of trailers</Label>
                  <Input
                    id="trailer_count"
                    type="number"
                    min="1"
                    max="4"
                    step="1"
                    className="mt-1 w-28"
                    value={form.trailer_count}
                    onChange={e => setForm({ ...form, trailer_count: e.target.value })}
                    data-testid="trailer-count-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    How many trailer units make up this asset type (e.g. B-Double = 2, Road Train = 3–4). Dispatchers will allocate this many trailers plus the prime mover and one driver.
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt">
                {editingType ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="archive-asset-type-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-destructive" />
              Archive Asset Type
            </AlertDialogTitle>
            <AlertDialogDescription>
              Archive &ldquo;{deleteTarget?.name}&rdquo;? Archived types are deactivated and hidden from new bookings but existing equipment records are preserved. You can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-archive-asset-type-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-archive-asset-type-button">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AssetTypesTab;
